import React, { useContext, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import Checkbox from 'src/components/form-components/Checkbox/Checkbox';
import CountrySelect from 'src/components/form-components/CountrySelect/CountrySelect';
import TextInput, { createInputId } from 'src/components/form-components/TextInput/TextInput';
import ValidationMessage from 'src/components/form-components/ValidationMessage/ValidationMessage';
import { AppContext } from 'src/contexts/AppContext';
import { IUserInvoice } from 'src/entities/user';
import { IValidatable } from 'src/services/formValidator';
import { invoiceDetailsFields } from 'src/services/validations/invoiceDetailsRules';

const messages = defineMessages({
    invoiceName: {
        id: 'invoiceName',
        defaultMessage: 'Számlázási név'
    },
    country: {
        id: 'country',
        defaultMessage: 'Ország'
    },
    city: {
        id: 'city',
        defaultMessage: 'Település'
    },
    streetAddress: {
        id: 'streetAddress',
        defaultMessage: 'Utca, házszám'
    },
    postalCode: {
        id: 'postalCode',
        defaultMessage: 'Irányítószám'
    },
    privatePerson: {
        id: 'privatePerson',
        defaultMessage: 'Magánszemély'
    },
    taxNumber: {
        id: 'taxNumber',
        defaultMessage: 'Adószám'
    }
})

interface IInvoiceDetailsProps {
    defaultUserInvoice: IUserInvoice;
    handleInvoiceChange: (value: string | boolean, name: string) => void;
}

const UserInvoiceDetails: React.FC<IInvoiceDetailsProps & IValidatable> = (props) => {

    const { company } = useContext(AppContext);
    const { handleInvoiceChange, defaultUserInvoice, validation } = props;
    const { InvoiceName, Country, City, StreetAddress, PostalCode, PrivatePerson, TaxNumber } = invoiceDetailsFields;

    useEffect(() => {
        if (defaultUserInvoice.Country === '') {
            const defaultInvoicingCountryCode = company?.PaymentSettings.DefaultInvoiceCountryCode ?? 'HU';
            handleInvoiceChange(defaultInvoicingCountryCode, 'Country');
        }
    }, [defaultUserInvoice.Country])

    function handleTextInputChange(value: string, field: string) {
        handleInvoiceChange(value, field);
    }

    function handleCheckboxChange(checked: boolean) {
        handleInvoiceChange(checked, PrivatePerson);
    }

    const createInputIds = () => {
        return {
            nameId: createInputId(InvoiceName),
            countryId: createInputId(Country),
            cityId: createInputId(City),
            streetAddressId: createInputId(StreetAddress),
            postalCodeId: createInputId(PostalCode),
            privatePersonId: createInputId(PrivatePerson),
            taxnumberId: createInputId(TaxNumber),
        }
    }

    const ids = createInputIds();

    return (
        <div className='invoice-details-form'>
            <TextInput
                className='invoice-details-form__field'
                fieldName={InvoiceName}
                value={defaultUserInvoice.InvoiceName}
                onChange={handleTextInputChange}
                label={messages.invoiceName}
                required={true}
                id={ids.nameId}
            />
            <ValidationMessage
                message={validation?.fields[InvoiceName]?.message}
                formatValues={validation?.fields[InvoiceName]?.messageValues}
                className="invoice-details-form__validation"
            />
            <CountrySelect
                className='invoice-details-form__field'
                fieldName={Country}
                defaultValue={defaultUserInvoice.Country ?? company?.PaymentSettings.DefaultInvoiceCountryCode}
                onChange={handleTextInputChange}
                label={messages.country}
                id={ids.countryId}
                disabled={true}
            />
            <ValidationMessage
                message={validation?.fields[Country]?.message}
                formatValues={validation?.fields[Country]?.messageValues}
                className="invoice-details-form__validation"
            />
            <TextInput
                className='invoice-details-form__field'
                fieldName={City}
                value={defaultUserInvoice.City}
                onChange={handleTextInputChange}
                label={messages.city}
                required={true}
                id={ids.cityId}
            />
            <ValidationMessage
                message={validation?.fields[City]?.message}
                formatValues={validation?.fields[City]?.messageValues}
                className="invoice-details-form__validation"
            />
            <TextInput
                className='invoice-details-form__field'
                fieldName={StreetAddress}
                value={defaultUserInvoice.StreetAddress}
                onChange={handleTextInputChange}
                label={messages.streetAddress}
                required={true}
                id={ids.streetAddressId}
            />
            <ValidationMessage
                message={validation?.fields[StreetAddress]?.message}
                formatValues={validation?.fields[StreetAddress]?.messageValues}
                className="invoice-details-form__validation"
            />
            <TextInput
                className='invoice-details-form__field'
                fieldName={PostalCode}
                value={defaultUserInvoice.PostalCode}
                onChange={handleTextInputChange}
                label={messages.postalCode}
                required={true}
                id={ids.postalCodeId}
            />
            <ValidationMessage
                message={validation?.fields[PostalCode]?.message}
                formatValues={validation?.fields[PostalCode]?.messageValues}
                className="invoice-details-form__validation"
            />
            <Checkbox
                className='invoice-details-form__field'
                fieldName={PrivatePerson}
                checked={defaultUserInvoice.PrivatePerson}
                onChange={handleCheckboxChange}
                key={ids.privatePersonId}
            >
                <FormattedMessage {...messages.privatePerson} />
            </Checkbox>
            {!defaultUserInvoice.PrivatePerson &&
                <>
                    <TextInput
                        className='invoice-details-form__field'
                        fieldName={TaxNumber}
                        value={defaultUserInvoice.TaxNumber}
                        onChange={handleTextInputChange}
                        label={messages.taxNumber}
                        id={ids.taxnumberId}
                    />
                    <ValidationMessage
                        message={validation?.fields[TaxNumber]?.message}
                        formatValues={validation?.fields[TaxNumber]?.messageValues}
                        className="invoice-details-form__validation"
                    />
                </>
            }
        </div>
    )
}

export default UserInvoiceDetails;